<script>
import { closePanel, showPanel } from '@phoenix/helpers/panel-helper';
import MixedGridFilters from '@phoenix/mixins/components/mixedGrid/MixedGridFilters';
import { filter, has } from 'lodash-es';
import Panel from '../common/Panel';
import MixedGridFiltersItemList from './MixedGridFiltersItemList';
import MixedGridFiltersItemRange from './MixedGridFiltersItemRange';
import MixedGridFiltersSelected from './MixedGridFiltersSelected';
import MixedGridSortSelect from './MixedGridSortSelect';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { PhxsgDivider } from '@sg/src/View/Components/Atoms/Divider';

export default {
  components: {
    MixedGridSortSelect,
    MixedGridFiltersItemList,
    MixedGridFiltersItemRange,
    MixedGridFiltersSelected,
    Panel,
    PhxsgButton,
    PhxsgDivider,
  },

  mixins: [MixedGridFilters],

  props: {
    id: {
      default: 'mixed-grid-filters',
      type: String,
    },
    extraClasses: {
      type: String,
      default: 'mt-base md:mt-base lg:mt-lg mb-base',
    },
    filters: {
      required: false,
      default: () => [],
      type: Array,
    },
    noveltyFilterId: {
      required: true,
      type: String,
    },
    noveltyProductPropertyValueIds: {
      required: true,
      type: Array,
    },
    shouldDisplayAvailableOnline: {
      type: Boolean,
      default: true,
    },
    shouldDisplayProductsCountOnlyWhenFiltered: {
      type: Boolean,
      default: false,
    },
    showSortSelect: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
  },

  emits: ['onSortChange', 'onFiltersChanged', 'filtersInitialized'],

  computed: {
    panelFilters() {
      return filter(this.filters, (filter) => {
        if (filter.id === this.noveltyFilterId) {
          return false;
        }

        if (filter.component === 'range') {
          return true;
        }

        return filter.items && filter.items.length > 0;
      });
    },

    hasFilters() {
      return this.filters.length > 0;
    },

    hasProductCountDisplay() {
      if (!this.shouldDisplayProductsCountOnlyWhenFiltered) {
        return this.shouldDisplayProductsCount;
      }

      return this.hasFilters;
    },

    panelId() {
      return `${this.id}-panel`;
    },

    panelTitle() {
      return this.title ?? this.$t('Product.filters');
    },

    productCountLabel() {
      return this.productsCount > 0
        ? `${this.productsCount} ${this.$t('Products.filters.resultsNoun')}`
        : this.$t('Products.filters.noResults');
    },

    shouldDisplayNoveltyFilter() {
      return this.noveltyProductPropertyValueIds.length > 0;
    },

    shouldDisplayQuickActions() {
      return (this.shouldDisplaySellable && this.shouldDisplayAvailableOnline) || this.shouldDisplayNoveltyFilter;
    },

    novelty: {
      get() {
        return has(this.activeFilters, this.noveltyFilterId);
      },
      set(value) {
        this.setCheckedFilter(value, this.noveltyFilterId, this.noveltyProductPropertyValueIds);
      },
    },
  },

  methods: {
    closePanel,
    showPanel,
  },
};
</script>

<template>
  <div class="filters" :class="extraClasses">
    <div class="filters__bar">
      <div class="filters__actions">
        <phxsg-button v-if="hasFilters" variant="secondary" @click.prevent="showPanel(panelId)">
          {{ $t('Product.filters') }}
        </phxsg-button>
        <mixed-grid-sort-select v-if="showSortSelect" v-model="currentSort"></mixed-grid-sort-select>
        <slot name="extra-actions" />
      </div>
      <div v-if="shouldDisplayQuickActions" class="filters__quick-actions">
        <div v-if="shouldDisplaySellable && shouldDisplayAvailableOnline" class="switch">
          <input
            id="available-online"
            v-model="availableOnline"
            type="checkbox"
            class="switch__input"
            data-cy="mixed-grid-available-online"
          />
          <label id="available-online-label" for="available-online" class="input-label switch__label checkbox-label">
            <span class="input-label__text">
              {{ $t('Products.filters.availableOnline') }}
            </span>
          </label>
        </div>
        <div v-if="shouldDisplayNoveltyFilter" class="switch">
          <input id="novelty" v-model="novelty" type="checkbox" class="switch__input" data-cy="mixed-grid-novelty" />
          <label id="novelty-label" for="novelty" class="input-label switch__label checkbox-label">
            <span class="input-label__text">
              {{ $t('Products.filters.novelties') }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
  <mixed-grid-filters-selected
    :filters="filters"
    :active-filters="activeFilters"
    :novelty-filter-id="noveltyFilterId"
    @remove-filter="removeFilter"
    @remove-all-filters="removeAllFilters"
  />
  <div v-if="hasProductCountDisplay" class="filters__result-count" data-cy="mixed-grid-products-count">
    {{ productCountLabel }}
  </div>

  <teleport to="#teleport-target-panels">
    <panel :id="panelId" :classes="['products-filters-panel']" :title="panelTitle" data-cy="mixed-grid-filters">
      <template #content>
        <template v-for="(filter, key, index) in panelFilters" :key="`filter-${filter.id}`">
          <h6 class="products-filters-panel__title">{{ filter.label }}</h6>
          <mixed-grid-filters-item-list
            v-if="filter.component === 'list'"
            :filter="filter"
            :active-filters="activeFilters"
            @on-item-selected="toggleFilter"
          />
          <mixed-grid-filters-item-range
            v-if="filter.component === 'range'"
            centered
            :filter="filter"
            :symbol="getSymbolFromFilter(filter)"
            :active-filters="activeFilters"
            @on-range-change="onRangeChange"
          />
          <phxsg-divider v-if="index < Object.keys(filters).length - 1" />
        </template>
      </template>
      <template #footer>
        <phxsg-button full :data-tracking="datalayerTrackingViewResults" @click.prevent="closePanel(panelId)">
          {{ $t('Button.showResults') }}
        </phxsg-button>
      </template>
    </panel>
  </teleport>
</template>
